import { gql, TypedDocumentNode } from "@apollo/client";

import {
  BitbucketCloudIntegration,
  BitbucketDatacenterIntegration,
  GithubEnterpriseIntegration,
  GitlabIntegration,
  MutationAzureDevOpsRepoIntegrationTestArgs,
  MutationBitbucketCloudIntegrationRegenerateWebhookSecretArgs,
  MutationBitbucketCloudIntegrationTestArgs,
  MutationBitbucketDatacenterIntegrationRegenerateWebhookSecretArgs,
  MutationBitbucketDatacenterIntegrationTestArgs,
  MutationGithubEnterpriseIntegrationRegenerateWebhookSecretArgs,
  MutationGithubEnterpriseIntegrationTestArgs,
  MutationGitlabIntegrationRegenerateWebhookSecretArgs,
  MutationGitlabIntegrationTestArgs,
  VcsIntegrationDetails,
} from "types/generated";

type VcsIntegrationDetailsFragmentData = {
  details: VcsIntegrationDetails | null;
};

export const vcsIntegrationDetailsFragment: TypedDocumentNode<VcsIntegrationDetailsFragmentData> = gql`
  fragment vcsIntegrationDetails on VCSIntegration {
    details {
      __typename
      ... on AzureDevOpsRepoIntegration {
        vcsChecks
        useGitCheckout
        description
        id
        isDefault
        labels
        name
        organizationURL
        space {
          id
          name
        }
        userFacingHost
        webhookPassword
        webhookUrl
      }
      ... on BitbucketCloudIntegration {
        vcsChecks
        useGitCheckout
        description
        id
        isDefault
        labels
        name
        space {
          id
          name
        }
        username
        webhookSecret
        webhookUrl
        email
      }
      ... on BitbucketDatacenterIntegration {
        vcsChecks
        useGitCheckout
        apiHost
        description
        id
        isDefault
        labels
        name
        space {
          id
          name
        }
        userFacingHost
        username
        webhookSecret
        webhookURL
      }
      ... on GithubEnterpriseIntegration {
        vcsChecks
        useGitCheckout
        apiHost
        appID
        appUrl
        description
        githubAppStatus
        id
        installations {
          installationId
        }
        isDefault
        labels
        name
        userFacingHost
        webhookUrl
        webhookSecret
      }
      ... on GitlabIntegration {
        vcsChecks
        useGitCheckout
        apiHost
        description
        id
        isDefault
        labels
        name
        space {
          id
          name
        }
        userFacingHost
        webhookSecret
        webhookUrl
      }
    }
  }
`;

type TestAzureDevOpsIntegrationData = {
  azureDevOpsRepoIntegrationTest: boolean;
};

export const TEST_AZURE_DEVOPS_INTEGRATION: TypedDocumentNode<
  TestAzureDevOpsIntegrationData,
  MutationAzureDevOpsRepoIntegrationTestArgs
> = gql`
  mutation TestAzureIntegrationInVCS($id: ID!) {
    azureDevOpsRepoIntegrationTest(id: $id)
  }
`;

type TestBitbucketCloudIntegrationData = {
  bitbucketCloudIntegrationTest: boolean;
};

export const TEST_BITBUCKET_CLOUD_INTEGRATION: TypedDocumentNode<
  TestBitbucketCloudIntegrationData,
  MutationBitbucketCloudIntegrationTestArgs
> = gql`
  mutation TestBitbucketCloudIntegrationInVCS($id: ID!) {
    bitbucketCloudIntegrationTest(id: $id)
  }
`;

type TestBitbucketDatacenterIntegrationData = {
  bitbucketDatacenterIntegrationTest: boolean;
};

export const TEST_BITBUCKET_DATACENTER_INTEGRATION: TypedDocumentNode<
  TestBitbucketDatacenterIntegrationData,
  MutationBitbucketDatacenterIntegrationTestArgs
> = gql`
  mutation TestBitbucketInVCS($id: ID!) {
    bitbucketDatacenterIntegrationTest(id: $id)
  }
`;

type TestGithubEnterpriseIntegrationData = {
  githubEnterpriseIntegrationTest: boolean;
};

export const TEST_GITHUB_INTEGRATION: TypedDocumentNode<
  TestGithubEnterpriseIntegrationData,
  MutationGithubEnterpriseIntegrationTestArgs
> = gql`
  mutation TestGitHubInVCS($id: ID!) {
    githubEnterpriseIntegrationTest(id: $id)
  }
`;

type TestGitlabIntegrationData = {
  gitlabIntegrationTest: boolean;
};

export const TEST_GITLAB_INTEGRATION: TypedDocumentNode<
  TestGitlabIntegrationData,
  MutationGitlabIntegrationTestArgs
> = gql`
  mutation TestGitLabInVCS($id: ID!) {
    gitlabIntegrationTest(id: $id)
  }
`;

type RegenerateBitbucketCloudIntegrationWebhookSecretData = {
  bitbucketCloudIntegrationRegenerateWebhookSecret: Pick<
    BitbucketCloudIntegration,
    "id" | "webhookSecret"
  >;
};
export const REGENERATE_BITBUCKET_CLOUD_INTEGRATION_WEBHOOK_SECRET: TypedDocumentNode<
  RegenerateBitbucketCloudIntegrationWebhookSecretData,
  MutationBitbucketCloudIntegrationRegenerateWebhookSecretArgs
> = gql`
  mutation RegenerateBitbucketCloudIntegrationWebhookSecret($id: ID!) {
    bitbucketCloudIntegrationRegenerateWebhookSecret(id: $id) {
      id
      webhookSecret
    }
  }
`;

type RegenerateBitbucketDatacenterIntegrationWebhookSecretData = {
  bitbucketDatacenterIntegrationRegenerateWebhookSecret: Pick<
    BitbucketDatacenterIntegration,
    "id" | "webhookSecret"
  >;
};
export const REGENERATE_BITBUCKET_DATACENTER_INTEGRATION_WEBHOOK_SECRET: TypedDocumentNode<
  RegenerateBitbucketDatacenterIntegrationWebhookSecretData,
  MutationBitbucketDatacenterIntegrationRegenerateWebhookSecretArgs
> = gql`
  mutation RegenerateBitbucketDatacenterIntegrationWebhookSecret($id: ID!) {
    bitbucketDatacenterIntegrationRegenerateWebhookSecret(id: $id) {
      id
      webhookSecret
    }
  }
`;

type RegenerateGithubEnterpriseIntegrationWebhookSecretData = {
  githubEnterpriseIntegrationRegenerateWebhookSecret: Pick<
    GithubEnterpriseIntegration,
    "id" | "webhookSecret"
  >;
};
export const REGENERATE_GITHUB_ENTERPRISE_INTEGRATION_WEBHOOK_SECRET: TypedDocumentNode<
  RegenerateGithubEnterpriseIntegrationWebhookSecretData,
  MutationGithubEnterpriseIntegrationRegenerateWebhookSecretArgs
> = gql`
  mutation RegenerateGithubEnterpriseIntegrationWebhookSecret($id: ID!) {
    githubEnterpriseIntegrationRegenerateWebhookSecret(id: $id) {
      id
      webhookSecret
    }
  }
`;

type RegenerateGitLabIntegrationWebhookSecretData = {
  gitlabIntegrationRegenerateWebhookSecret: Pick<GitlabIntegration, "id" | "webhookSecret">;
};
export const REGENERATE_GITLAB_INTEGRATION_WEBHOOK_SECRET: TypedDocumentNode<
  RegenerateGitLabIntegrationWebhookSecretData,
  MutationGitlabIntegrationRegenerateWebhookSecretArgs
> = gql`
  mutation RegenerateGitlabIntegrationWebhookSecret($id: ID!) {
    gitlabIntegrationRegenerateWebhookSecret(id: $id) {
      id
      webhookSecret
    }
  }
`;
